/**
 * A link into a project in another of the person's companies switches to that company first
 * (SB-321; UDE's half is SB-319).
 *
 * Every project page goes through here when it loads. While sign-in is on, it asks UDE for the
 * project once before the page renders: the session's own company renders at once, and a 409
 * `project_in_other_company` switches the session and loads the page again. Anything else
 * renders the page, which says what went wrong in its own words. A background request never
 * switches (see `company.ts`).
 *
 * A project that passed is not asked about again until the page loads anew: the company changes
 * only through a page load, so moving between its steps renders at once.
 */
import {ReactNode, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {ude} from '../api/client';
import {LoadingState} from '../components/shared';
import {useAuth} from './AuthContext';
import {projectCompanyOf, switchForLink} from './company';

const opened = new Set<string>();

export default function ProjectCompanyGate({children}: {children: ReactNode}) {
    const {id} = useParams<{id: string}>();
    const {authEnabled} = useAuth();
    const [state, setState] = useState<'checking' | 'switching' | 'open'>(
        authEnabled && id && !opened.has(id) ? 'checking' : 'open');

    useEffect(() => {
        if (!authEnabled || !id || opened.has(id)) {
            setState('open');
            return;
        }
        let cancelled = false;
        setState('checking');
        (async () => {
            try {
                await ude.getProject(id);
                opened.add(id);
            } catch (error) {
                const company = projectCompanyOf(error);
                if (company && !cancelled) {
                    setState('switching');
                    if (await switchForLink(company)) {
                        window.location.reload();
                        return;
                    }
                }
            }
            if (!cancelled) setState('open');
        })();
        return () => {
            cancelled = true;
        };
    }, [authEnabled, id]);

    if (state === 'checking') return null;
    if (state === 'switching') return <LoadingState message="Prosjektet ligger i et annet av firmaene dine. Bytter firma …"/>;
    return <>{children}</>;
}
